import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { X, Minus, Plus, ShoppingBag, Check, Star, ArrowRight } from 'lucide-react';
import { useCart } from '../../lib/cartContext';
import { productReviewsApi } from '../../lib/api';
import { useRecentlyViewedProducts } from '../../lib/useRecentlyViewedProducts';

export default function QuickViewModal({ product, onClose }) {
  const { addItem, loading: cartLoading } = useCart();
  const { trackView } = useRecentlyViewedProducts();
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [isAdding, setIsAdding] = useState(false);
  const [added, setAdded] = useState(false);
  const [reviewStats, setReviewStats] = useState(null);

  useEffect(() => {
    if (!product) return;
    trackView(product);
    productReviewsApi.getReviews(product.id).then(res => {
      if (res.data?.stats?.total > 0) setReviewStats(res.data.stats);
    }).catch(() => {});
  }, [product, trackView]);

  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  // Lock body scroll
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const handleAddToCart = async () => {
    setIsAdding(true);
    for (let i = 0; i < quantity; i++) {
      await addItem(product.id);
    }
    setIsAdding(false);
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  if (!product) return null;

  const images = product.images?.length > 0 ? product.images : ['https://placehold.co/600x600/0a0a0a/ffffff?text=Product'];
  const hasDiscount = product.compare_at_price && product.compare_at_price > product.price;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 animate-fade-in"
        onClick={onClose}
        data-testid="quickview-backdrop"
      />

      {/* Modal */}
      <div
        className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none"
        data-testid="quickview-modal"
      >
        <div
          className="relative bg-[#0A0A0A] border border-zinc-800 w-full max-w-3xl max-h-[85vh] overflow-y-auto pointer-events-auto animate-scale-in"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-10 w-8 h-8 flex items-center justify-center text-zinc-400 hover:text-white bg-black/50 transition-colors"
            data-testid="quickview-close"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="grid grid-cols-1 md:grid-cols-2">
            {/* Image Gallery */}
            <div className="bg-zinc-900 p-6">
              <div className="aspect-square overflow-hidden mb-3">
                <img
                  src={images[selectedImage]}
                  alt={product.name}
                  className="w-full h-full object-contain"
                />
              </div>
              {images.length > 1 && (
                <div className="flex gap-2 overflow-x-auto">
                  {images.map((img, i) => (
                    <button
                      key={img}
                      onClick={() => setSelectedImage(i)}
                      className={`w-14 h-14 flex-shrink-0 overflow-hidden border-2 transition-colors ${
                        selectedImage === i ? 'border-[#E63946]' : 'border-transparent hover:border-zinc-600'
                      }`}
                      data-testid={`quickview-thumb-${i}`}
                    >
                      <img src={img} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Product Info */}
            <div className="p-6 flex flex-col">
              <span className="text-zinc-500 text-xs uppercase tracking-wider">{product.category}</span>
              <h2
                className="text-white text-2xl mt-1 mb-1"
                style={{ fontFamily: 'Bebas Neue, sans-serif' }}
                data-testid="quickview-name"
              >
                {product.name}
              </h2>

              {/* Star Rating */}
              {reviewStats && (
                <div className="flex items-center gap-2 mb-3" data-testid="quickview-rating">
                  <div className="flex gap-0.5">
                    {[1, 2, 3, 4, 5].map(i => (
                      <Star
                        key={i}
                        className={`w-3.5 h-3.5 ${i <= Math.round(reviewStats.average_rating) ? 'text-[#E63946] fill-[#E63946]' : 'text-zinc-700'}`}
                      />
                    ))}
                  </div>
                  <span className="text-zinc-500 text-xs">({reviewStats.total})</span>
                </div>
              )}

              {/* Price */}
              <div className="flex items-center gap-3 mb-4">
                <span className="text-[#E63946] text-2xl font-semibold">${product.price?.toFixed(2)}</span>
                {hasDiscount && (
                  <>
                    <span className="text-zinc-600 line-through">${product.compare_at_price.toFixed(2)}</span>
                    <span className="px-2 py-0.5 text-[10px] uppercase tracking-wider bg-[#E63946] text-white">
                      Save ${(product.compare_at_price - product.price).toFixed(2)}
                    </span>
                  </>
                )}
              </div>

              {/* Description */}
              {product.description && (
                <p className="text-zinc-400 text-sm leading-relaxed mb-6 line-clamp-3">{product.description}</p>
              )}

              {/* Quantity & Add to Cart */}
              <div className="mt-auto space-y-4">
                <div className="flex items-center gap-4">
                  <div className="flex items-center border border-zinc-700">
                    <button
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      className="w-10 h-10 flex items-center justify-center text-zinc-400 hover:text-white transition-colors"
                      data-testid="quickview-decrease"
                    >
                      <Minus className="w-4 h-4" />
                    </button>
                    <span className="w-10 text-center text-white text-sm">{quantity}</span>
                    <button
                      onClick={() => setQuantity(quantity + 1)}
                      className="w-10 h-10 flex items-center justify-center text-zinc-400 hover:text-white transition-colors"
                      data-testid="quickview-increase"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>

                  <button
                    onClick={handleAddToCart}
                    disabled={isAdding || cartLoading}
                    className={`flex-1 py-3 px-6 uppercase tracking-widest text-sm font-bold flex items-center justify-center gap-2 transition-all duration-300 ${
                      added ? 'bg-green-600 text-white' : 'btn-ninja'
                    }`}
                    data-testid="quickview-add-to-cart"
                  >
                    {/* eslint-disable-next-line no-nested-ternary -- readable tier picker */}
                    {added ? (
                      <><Check className="w-4 h-4" /> Added!</>
                    ) : isAdding ? (
                      'Adding...'
                    ) : (
                      <><ShoppingBag className="w-4 h-4" /> Add to Cart</>
                    )}
                  </button>
                </div>

                <Link
                  to={`/shop/${product.slug}`}
                  onClick={onClose}
                  className="flex items-center justify-center gap-2 text-zinc-400 hover:text-white text-sm transition-colors py-2"
                  data-testid="quickview-full-details"
                >
                  View Full Details <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
